import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { shallowEqual, useSelector } from 'react-redux';
import { checkEnvPurpleZone, getCookie, getSearchParams, setCookie } from '../common/Utils';
import { PURPLEZONE_SERVER_ID_KEY } from '../common/constants';
import { Pagination } from '../components/Pagination';
import { IServerDDProp, ServerDropdown } from '../components/ServerDropdown';
import useAxios from '../hook/useAxios';
import useRouter from '../hook/useRouter';
import { ISearchParam } from '../interface/ICommon';
import { IPriceItemList } from '../interface/IPrice';
import { RootState, store, useAppDispatch } from '../store';
import { setPriceParams } from '../store/priceSlice';
import { PriceList } from './PriceList';
import { ResultWrapFavorite } from './ResultWrapFavorite';

const makeParams = (_obj: ISearchParam, _key: string, _val: string | number) => {
    if (_val) {
        _obj[_key] = _val;
    }
};

const getFirstSvrId = (_service: string, _paramSvrid: string) => {
    if (_paramSvrid) {
        return _paramSvrid;
    }

    if (checkEnvPurpleZone()) {
        // case: 퍼플존 (페이지 진입시, sessionStorage에 저장된 pzsid, pzcid 값 사용)
        const pzsid = sessionStorage.getItem(PURPLEZONE_SERVER_ID_KEY);
        if (pzsid) return pzsid;
    }

    if (window.userData?.gameAccount?.serverId) {
        return window.userData.gameAccount.serverId;
    }

    const recentSvrId = getCookie('gameinforecentsvrid' + _service);
    if (recentSvrId !== false) {
        return recentSvrId as string;
    }

    return '';
};

const getParamSvrId = () => {
    const searchParams = getSearchParams(document.location.href);
    return searchParams ? searchParams.serverid : '';
};

export const ResultWrap = () => {
    const l10n = useSelector((state: RootState) => state.main.l10n);
    const config = store.getState().main.config;
    const { res, loading, error, fetchData } = useAxios<IPriceItemList>();
    const dispatch = useAppDispatch();
    const priceParams = useSelector((state: RootState) => state.price.priceParams, shallowEqual);
    const service = useSelector((state: RootState) => state.main.config.service);
    const [svrid, svridSet] = useState('');
    const { params, getValue, changeParam } = useRouter([
        'serverid',
        'class',
        'grade',
        'sale',
        'category1',
        'favorite',
        'bless',
        'category2',
        'enchantfrom',
        'enchantto',
        'query',
        'page',
        'exactsearch',
    ]);
    const firSvrId = useMemo(() => getFirstSvrId(service, getParamSvrId()), []);

    useEffect(() => {
        if (!params) return;

        let svr_id = getValue('serverid');
        if (svr_id === '') svr_id = firSvrId;
        svridSet(svr_id);

        let para = { size: 30 };
        makeParams(para, 'serverId', svr_id === 'all' ? '' : svr_id);
        makeParams(para, 'classes', getValue('class'));
        makeParams(para, 'grades', getValue('grade'));
        makeParams(para, 'sale', getValue('sale') || 'true');
        makeParams(para, 'category1', getValue('category1'));
        makeParams(para, 'category2', getValue('category2'));
        makeParams(para, 'fromEnchantLevel', getValue('enchantfrom'));
        makeParams(para, 'toEnchantLevel', getValue('enchantto'));
        makeParams(para, 'searchKeyword', getValue('query'));
        makeParams(para, 'blessedType', getValue('bless'));
        makeParams(para, 'page', getValue('page'));
        makeParams(para, 'favorite', getValue('favorite'));
        makeParams(para, 'exact', getValue('exactsearch'));

        dispatch(setPriceParams(para));
    }, [params]);

    useEffect(() => {
        if (!priceParams.size) return;
        fetchData({
            method: 'get',
            config: store.getState().main.config,
            l10n: l10n,
            path: `/${config.service}/v2.0/price/search/item`,
            param: priceParams,
        });
    }, [priceParams]);

    useEffect(() => {
        if (error) {
            toast.dismiss();
            toast(
                (t) => (
                    <div className="toast-wrapper">
                        <span className="toast-title" dangerouslySetInnerHTML={{ __html: error.msg }}></span>
                    </div>
                ),
                {
                    duration: 3000,
                    className: 'gameinfo-toast',
                    position: 'bottom-center',
                }
            );
        }
    }, [error]);

    const svrDropdownProp: IServerDDProp = {
        defaultSvrId: firSvrId,
        changeSvrId: svrid,
        tradableWorld: false,
        useAllSvrBtn: true,
        useWorldSvr: false,
        submitCallback: (_svr: string) => {
            changeParam({ serverid: _svr, page: '' });
            setCookie('gameinforecentsvrid' + service, _svr, 7, '.plaync.com');
        },
    };

    if (!res) return null;

    return (
        <div className="gameinfo-result-wrapper">
            <div className="gameinfo-result">
                <section className="gameinfo-result__util">
                    <ServerDropdown props={svrDropdownProp} />
                    <div className="title">
                        {l10n['noti.searchresults']}&nbsp;
                        <em
                            dangerouslySetInnerHTML={{
                                __html: l10n['noti.per'].replace(
                                    '{N}',
                                    `${res.pagination.total > res.pagination.limit ? res.pagination.limit + '+' : res.pagination.total}`
                                ),
                            }}
                        ></em>
                    </div>
                    <ResultWrapFavorite />
                </section>

                <PriceList listData={res.contents} priceListParam={priceParams} loading={loading} firstSvrId={firSvrId} />

                {res.contents && res.contents.length > 0 && (
                    <>
                        {service === 'l2m' && (
                            <a className="link-developers" href="https://developers.plaync.com/" style={{ display: 'flex' }}>
                                아이템 시세 오픈 API 이용하기
                            </a>
                        )}
                        <Pagination dat={res.pagination} />
                    </>
                )}
            </div>
        </div>
    );
};
